import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  updateDoc, 
  doc 
} from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebaseConfig';
import { useNotifications } from '../contexts/NotificationContext';
import { 
  FaBell, 
  FaComment, 
  FaHeart, 
  FaAt, 
  FaCrown 
} from 'react-icons/fa';

const NotificationIcons = {
  'mention': FaAt,
  'comment': FaComment,
  'like': FaHeart,
  'post_created': FaBell,
  'pro_upgrade': FaCrown,
  'default': FaBell
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { markNotificationAsSeen } = useNotifications();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    if (!auth.currentUser) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef, 
        where('recipientId', '==', auth.currentUser.uid),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const fetchedNotifications = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setNotifications(fetchedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark notification as seen
      await markNotificationAsSeen(notification.id);

      // Navigate based on notification type
      switch (notification.type) {
        case 'mention':
        case 'comment':
          // Navigate to specific post
          if (notification.relatedId) {
            navigate(`/post/${notification.relatedId}`);
          }
          break;
        case 'pro_upgrade':
          navigate('/profile');
          break;
        default:
          // Generic fallback
          navigate('/');
      }
    } catch (error) {
      console.error('Error handling notification:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-white mb-6">Notifications</h1>
      
      {notifications.length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          <FaBell className="mx-auto text-4xl mb-4 text-gray-500" />
          <p>No notifications yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {notifications.map((notification) => {
            const NotificationIcon = NotificationIcons[notification.type] || NotificationIcons['default'];
            
            return (
              <div 
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`
                  flex items-center p-4 rounded-lg cursor-pointer 
                  ${notification.seen ? 'bg-white/10' : 'bg-purple-500/20'}
                  hover:bg-white/20 transition-colors
                `}
              >
                <div className="mr-4">
                  <NotificationIcon 
                    className={`
                      text-2xl 
                      ${notification.type === 'mention' ? 'text-blue-400' : 
                        notification.type === 'like' ? 'text-red-400' : 
                        notification.type === 'comment' ? 'text-green-400' : 
                        'text-purple-400'}
                    `} 
                  />
                </div>
                <div className="flex-grow">
                  <p className="text-white">{notification.message}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(notification.createdAt?.toDate()).toLocaleString()}
                  </p>
                </div>
                {!notification.seen && (
                  <div className="w-3 h-3 bg-red-500 rounded-full ml-4"></div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Notifications;